/**
 * Minimal, allocation-light 3D math. Deliberately dependency-free so the same
 * code runs identically in the browser and in Node (shared simulation).
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export const copy = (out: Vec3, a: Vec3): Vec3 => {
  out.x = a.x;
  out.y = a.y;
  out.z = a.z;
  return out;
};

export const set = (out: Vec3, x: number, y: number, z: number): Vec3 => {
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
};

export const add = (out: Vec3, a: Vec3, b: Vec3): Vec3 =>
  set(out, a.x + b.x, a.y + b.y, a.z + b.z);

export const sub = (out: Vec3, a: Vec3, b: Vec3): Vec3 =>
  set(out, a.x - b.x, a.y - b.y, a.z - b.z);

export const scale = (out: Vec3, a: Vec3, s: number): Vec3 =>
  set(out, a.x * s, a.y * s, a.z * s);

/** out = a + b * s */
export const addScaled = (out: Vec3, a: Vec3, b: Vec3, s: number): Vec3 =>
  set(out, a.x + b.x * s, a.y + b.y * s, a.z + b.z * s);

export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

export const lengthSq = (a: Vec3): number => dot(a, a);

export const length = (a: Vec3): number => Math.sqrt(dot(a, a));

export const distance = (a: Vec3, b: Vec3): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

export const normalize = (out: Vec3, a: Vec3): Vec3 => {
  const len = length(a);
  return len > 1e-8 ? scale(out, a, 1 / len) : set(out, 0, 0, 0);
};

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const lerpVec3 = (out: Vec3, a: Vec3, b: Vec3, t: number): Vec3 =>
  set(out, lerp(a.x, b.x, t), lerp(a.y, b.y, t), lerp(a.z, b.z, t));

export const clamp = (v: number, min: number, max: number): number =>
  v < min ? min : v > max ? max : v;

/** Shortest signed difference between two angles, in radians. */
export const angleDelta = (from: number, to: number): number => {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
};

export const lerpAngle = (from: number, to: number, t: number): number =>
  from + angleDelta(from, to) * t;

/** Forward vector from yaw/pitch, in the engine's right-handed Y-up space. */
export const directionFromAngles = (out: Vec3, yaw: number, pitch: number): Vec3 => {
  const cp = Math.cos(pitch);
  return set(out, -Math.sin(yaw) * cp, Math.sin(pitch), -Math.cos(yaw) * cp);
};

/** Horizontal (XZ) basis vectors for movement input. */
export const yawBasis = (yaw: number): { forward: Vec3; right: Vec3 } => ({
  forward: vec3(-Math.sin(yaw), 0, -Math.cos(yaw)),
  right: vec3(Math.cos(yaw), 0, -Math.sin(yaw)),
});

/**
 * Deterministic PRNG (mulberry32). Any randomness that affects gameplay — spread
 * patterns, spawn selection — must come from a seeded stream so the client's
 * prediction matches the server's authoritative result.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
}
